// src/pages/dashboard/MenuPageEditor.jsx
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Save, Plus, Trash2, Loader2, Camera, Palette,
  QrCode, Copy, Check, Image as ImageIcon, UtensilsCrossed, Layers,
  ChevronUp, ChevronDown, Sparkles, Eye, ClipboardList,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import api from '../../lib/axios'
import { THEMES, FONTS } from '../../lib/themeConfig'
import { makeEmptyCategory, makeEmptyItem } from '../../lib/menuReviewConfig'
import { PRESET_LIST, ITEM_LAYOUTS } from '../../lib/menuDesign'
import { normalizeSettings, extractCategories } from '../../lib/normalizeSettings'
import MenuRenderer from '../../components/layout/MenuRenderer'
import MenuImportWizard from '../../components/menu/MenuImportWizard'

const DEFAULT_MENU_SETTINGS = {
  theme: 'classic',
  font: 'inter',
  layout: 'classic',
  customColors: false,
  customBg: '#0F1B3D',
  customAccent: '#14B8A6',
  customText: '#FFFFFF',
  cardColorEnabled: false,
  cardColor: '#FFFFFF',
  cardBorderColor: '#E2E8F0',
  // Commandes
  orderEnabled: true,
  whatsapp: '',
  askTable: true,
  tagline: '',
  // Design professionnel
  designPreset: 'dar-zayna',
  itemLayout: 'list',
  heroImage: '',
  heroText: '',
  languages: ['fr'],
  // Infos pratiques (onglet « Plus »)
  infoPhone: '',
  infoAddress: '',
  infoHours: '',
  infoWifi: '',
  infoInstagram: '',
}

const TAG_OPTIONS = [
  ['popular', 'Populaire'], ['new', 'Nouveau'], ['vegetarian', 'Végétarien'],
  ['vegan', 'Vegan'], ['spicy', 'Épicé'], ['gluten-free', 'Sans gluten'], ['halal', 'Halal'],
]

// Générateur d'identifiants uniques : évite les collisions entre plats/catégories.
let menuIdCounter = 0
function createUniqueId(prefix = 'id') {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return `${prefix}-${cryptoApi.randomUUID()}`
  }
  menuIdCounter += 1
  return `${prefix}-${Date.now()}-${menuIdCounter}-${Math.random().toString(36).slice(2, 10)}`
}

function createCategory() {
  return { ...makeEmptyCategory(), id: createUniqueId('cat') }
}
function createItem() {
  return { ...makeEmptyItem(), id: createUniqueId('item') }
}

// Modèles prêts à l'emploi : ils changent uniquement le design, pas le contenu du menu.
const MENU_TEMPLATES = [
  {
    id: 'professional', name: 'Professionnel', description: 'Sobre, clair et moderne',
    settings: { theme: 'classic', font: 'inter', layout: 'classic', customColors: true,
      customBg: '#F8FAFC', customAccent: '#0F766E', customText: '#172033',
      cardColorEnabled: true, cardColor: '#FFFFFF', cardBorderColor: '#DDE5E7' },
  },
  {
    id: 'premium', name: 'Premium', description: 'Ivoire et doré élégant',
    settings: { theme: 'classic', font: 'playfair', layout: 'classic', customColors: true,
      customBg: '#F7F1E5', customAccent: '#A8792E', customText: '#2D261D',
      cardColorEnabled: true, cardColor: '#FFFCF5', cardBorderColor: '#D9C7A1' },
  },
  {
    id: 'extra-premium', name: 'Extra Premium', description: 'Noir profond et or',
    settings: { theme: 'classic', font: 'playfair', layout: 'classic', customColors: true,
      customBg: '#111111', customAccent: '#D6B56D', customText: '#F8F2E6',
      cardColorEnabled: true, cardColor: '#1D1D1D', cardBorderColor: '#6F5A32' },
  },
]

const inputCls =
  'w-full px-3 py-2 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20'
const smallCls =
  'w-full px-2 py-1.5 bg-slate-50 rounded-lg text-xs border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20'

function slugify(v) {
  return String(v || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}

function move(list, index, dir) {
  const t = index + dir
  if (t < 0 || t >= list.length) return list
  const c = [...list]
  ;[c[index], c[t]] = [c[t], c[index]]
  return c
}

// ─────────────────────────────────────────────
// Import IA : conversion du style détecté → réglages
// ─────────────────────────────────────────────
const isHex = (v) => /^#[0-9a-f]{6}$/i.test(v || '')

const lum = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const contrast = (a, b) => {
  const [l1, l2] = [lum(a), lum(b)].sort((x, y) => y - x)
  return (l1 + 0.05) / (l2 + 0.05)
}

// ⚠️ Adaptez ces ids à votre themeConfig.js (les ids inconnus sont ignorés)
const FONT_BY_STYLE = {
  sans: 'inter',
  serif: 'playfair',
  script: 'dancing',
  display: 'poppins',
}

function styleToSettings(style = {}) {
  const out = {}

  if (isHex(style.background)) {
    out.customColors = true
    out.customBg = style.background
    out.customAccent = isHex(style.accent) ? style.accent : DEFAULT_MENU_SETTINGS.customAccent
    const txt = isHex(style.text) ? style.text : '#FFFFFF'
    out.customText =
      contrast(style.background, txt) >= 4.5
        ? txt
        : lum(style.background) > 0.5 ? '#111111' : '#FFFFFF'
  }

  if (isHex(style.card)) {
    out.cardColorEnabled = true
    out.cardColor = style.card
    out.cardBorderColor = isHex(style.card_border) ? style.card_border : style.card
  }

  const fontId = FONT_BY_STYLE[style.font_style]
  if (fontId && FONTS.some((f) => f.id === fontId)) out.font = fontId

  return out
}

function ColorInput({ label, value, onChange }) {
  const safe = /^#[0-9a-f]{6}$/i.test(value || '') ? value : '#000000'
  return (
    <div>
      <label className="block text-[11px] font-semibold text-navy mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="color" value={safe} onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer shrink-0" />
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-2 py-2 bg-slate-50 rounded-lg text-xs border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
      </div>
    </div>
  )
}

export default function MenuPageEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id || id === 'new'

  const [isLoading, setIsLoading] = useState(!isNew)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showQrModal, setShowQrModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingItem, setUploadingItem] = useState(null)
  const [activeTab, setActiveTab] = useState('content')
  const [showImport, setShowImport] = useState(false)
  // < lg : un seul panneau à la fois (éditer OU aperçu). >= lg : les deux côte à côte.
  const [mobileView, setMobileView] = useState('edit')

  const logoInputRef = useRef(null)
  const itemImageInputRef = useRef(null)
  const uploadTargetRef = useRef(null)
  const heroInputRef = useRef(null)
  const [uploadingHero, setUploadingHero] = useState(false)

  const [page, setPage] = useState(() => ({
    title: 'Notre Menu',
    slug: '',
    logo: '',
    is_published: true,
    settings: { ...DEFAULT_MENU_SETTINGS },
    currency: 'DH',
    categories: [{ ...createCategory(), name: 'Entrées', items: [createItem()] }],
  }))

  const publicUrl = `${window.location.origin}/menu/${page.slug || 'votre-slug'}`
  const settings = page.settings

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000) }

  // Sur mobile, un message d'erreur est dans le panneau « Éditer » : on y revient pour le voir.
  useEffect(() => {
    if (error) setMobileView('edit')
  }, [error])

  const loadPage = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await api.get(`/pages/${id}`)
      const p = data?.data ?? data
      const s = normalizeSettings(p?.settings)
      const cats = extractCategories({ ...p, settings: s })
      const seenCategoryIds = new Set()
      const repairedCategories = cats.map((category) => {
        let categoryId = category.id
        if (!categoryId || seenCategoryIds.has(categoryId)) categoryId = createUniqueId('cat')
        seenCategoryIds.add(categoryId)
        const seenItemIds = new Set()
        const items = (category.items || []).map((item) => {
          let itemId = item.id
          if (!itemId || seenItemIds.has(itemId)) itemId = createUniqueId('item')
          seenItemIds.add(itemId)
          return { ...item, id: itemId }
        })
        return { ...category, id: categoryId, items }
      })
      const { categories: _c, currency: _cur, ...visual } = s
      setPage({
        title: p?.title || '',
        slug: p?.slug || '',
        logo: p?.avatar || '',
        is_published: !!p?.is_published,
        settings: { ...DEFAULT_MENU_SETTINGS, ...visual },
        currency: s.currency || 'DH',
        categories: repairedCategories.length ? repairedCategories : [createCategory()],
      })
    } catch (err) {
      console.error('Erreur loadPage:', err)
      setError('Impossible de charger la page.')
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (!isNew) loadPage()
  }, [isNew, loadPage])

  const handleSave = async () => {
    setError(''); setSuccess('')
    const categories = page.categories
      .map((c) => ({ ...c, items: (c.items || []).filter((it) => (it.name || '').trim()) }))
      .filter((c) => (c.name || '').trim() || c.items.length)

    if (!categories.length) {
      setError('Ajoutez au moins une catégorie avec un plat (le nom du plat est obligatoire).')
      setActiveTab('content')
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        type: 'menu',
        title: (page.title || '').trim() || 'Menu',
        slug: page.slug || slugify(page.title) || undefined,
        is_published: !!page.is_published,
        avatar: page.logo || null,
        settings: { ...page.settings, currency: page.currency, categories },
      }
      if (isNew) {
        const { data } = await api.post('/pages', payload)
        const newId = data?.data?.id || data?.id
        if (newId) navigate(`/app/menus/${newId}/edit`, { replace: true })
        else flash('✅ Menu créé !')
      } else {
        await api.put(`/pages/${id}`, payload)
        flash('✅ Menu sauvegardé !')
      }
    } catch (err) {
      console.error('ERREUR SAUVEGARDE:', err?.response?.data || err)
      const d = err?.response?.data
      setError(
        d?.error || d?.message ||
        (d?.errors ? Object.values(d.errors).flat().join(' • ') : '') ||
        err?.message || 'Erreur lors de la sauvegarde.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  // Ctrl/Cmd + S
  const saveRef = useRef(handleSave)
  saveRef.current = handleSave
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        saveRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const uploadFile = async (file, type) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)
    const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    return data.url
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadingLogo(true)
    try {
      const url = await uploadFile(file, 'avatar')
      setPage((p) => ({ ...p, logo: url }))
    } catch (err) {
      console.error(err); setError('Erreur upload logo.')
    } finally { setUploadingLogo(false) }
  }

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploadingHero(true)
    try {
      const url = await uploadFile(file, 'image')
      setPage((p) => ({ ...p, settings: { ...p.settings, heroImage: url } }))
    } catch (err) {
      console.error(err); setError('Erreur upload image.')
    } finally { setUploadingHero(false) }
  }

  const requestItemImageUpload = (catId, itemId) => {
    uploadTargetRef.current = { catId, itemId }
    itemImageInputRef.current?.click()
  }

  const handleItemImageUpload = async (e) => {
    const file = e.target.files?.[0]
    const target = uploadTargetRef.current
    e.target.value = ''
    if (!file || !target) return
    setUploadingItem(target.itemId)
    try {
      const url = await uploadFile(file, 'image')
      updateItem(target.catId, target.itemId, 'image', url)
    } catch (err) {
      console.error(err); setError('Erreur upload image.')
    } finally { setUploadingItem(null) }
  }

  const setCategories = (fn) => setPage((p) => ({ ...p, categories: fn(p.categories) }))

  const addCategory = () =>
    setCategories((cats) => [...cats, { ...createCategory(), items: [createItem()] }])

  // ★ IA : applique le résultat de l'import (contenu + design + logo)
  const handleImportDone = ({ restaurant, categories }) => {
    const imported = (categories || [])
      .map((c) => ({
        ...createCategory(),
        ...c,
        id: createUniqueId('cat'),
        items: (c.items || []).map((it) => ({
          ...createItem(),
          ...it,
          id: createUniqueId('item'),
        })),
      }))
      .filter((c) => c.items.length)

    if (!imported.length) {
      setShowImport(false)
      setError("Aucun plat n'a été détecté.")
      return
    }

    const hasContent = page.categories.some((c) =>
      (c.items || []).some((i) => (i.name || '').trim())
    )
    if (hasContent && !window.confirm('Remplacer le menu actuel ? (Annuler = ajouter à la suite)')) {
      setCategories((cats) => [...cats, ...imported])
    } else {
      setCategories(() => imported)
    }

    setPage((p) => {
      const title = restaurant?.name || p.title
      return {
        ...p,
        title,
        slug: p.slug || slugify(title),
        logo: restaurant?.logo || p.logo,
        currency: restaurant?.currency || p.currency,
        settings: {
          ...p.settings,
          ...styleToSettings(restaurant?.style),
          tagline: restaurant?.tagline || p.settings.tagline,
        },
      }
    })

    setShowImport(false)
    flash('✅ Menu importé ! Pensez à enregistrer.')
  }

  const removeCategory = (catId) => {
    const cat = page.categories.find((c) => c.id === catId)
    const n = (cat?.items || []).filter((i) => (i.name || '').trim()).length
    if (n && !window.confirm(`Supprimer cette catégorie et ses ${n} plat(s) ?`)) return
    setCategories((cats) => {
      const next = cats.filter((c) => c.id !== catId)
      return next.length ? next : [createCategory()]
    })
  }

  const updateCategory = (catId, field, value) =>
    setCategories((cats) => cats.map((c) => (c.id === catId ? { ...c, [field]: value } : c)))

  const mapItems = (catId, fn) =>
    setCategories((cats) => cats.map((c) => (c.id === catId ? { ...c, items: fn(c.items || []) } : c)))

  const addItem = (catId) => mapItems(catId, (items) => [...items, createItem()])
  const removeItem = (catId, itemId) => mapItems(catId, (items) => items.filter((it) => it.id !== itemId))
  const updateItem = (catId, itemId, field, value) =>
    mapItems(catId, (items) => items.map((it) => (it.id === itemId ? { ...it, [field]: value } : it)))

  const toggleTag = (catId, item, tag) => {
    const cur = item.tags || []
    updateItem(catId, item.id, 'tags', cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag])
  }

  const updateSettings = (key, value) =>
    setPage((p) => ({ ...p, settings: { ...p.settings, [key]: value } }))

  const copyLink = () => {
    try {
      navigator.clipboard.writeText(publicUrl)
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[#f9f9ff]">
        <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-[#f9f9ff] flex">
      {/* ═══ PANNEAU D'ÉDITION ═══ */}
      <aside className={`${mobileView === 'preview' ? 'hidden' : 'flex'} lg:flex w-full lg:w-[400px] bg-white lg:border-r border-slate-100 flex-col fixed left-0 top-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:bottom-0 z-20`}>
        <div className="p-3 sm:p-4 border-b border-slate-100 flex items-center justify-between gap-2">
          <button onClick={() => navigate('/app/pages')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="flex items-center gap-2">
            {!isNew && (
              <button onClick={() => navigate(`/app/menus/${id}/orders`)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-[#006b5f] rounded-lg hover:bg-slate-50">
                <ClipboardList className="w-4 h-4" /> Commandes
              </button>
            )}
            <button onClick={() => setShowQrModal(true)} className="p-2 text-slate-500 hover:text-[#006b5f] rounded-lg hover:bg-slate-50" aria-label="QR Code">
              <QrCode className="w-4 h-4" />
            </button>
            <button onClick={handleSave} disabled={isSaving} className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-[#006b5f] hover:bg-[#005a50] text-white rounded-lg font-semibold text-xs transition-colors disabled:opacity-60">
              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Enregistrer
            </button>
          </div>
        </div>

        <div className="flex border-b border-slate-100 px-2 sm:px-3 gap-1 overflow-x-auto scrollbar-thin">
          {[
            { id: 'content', label: 'Menu', icon: UtensilsCrossed },
            { id: 'design', label: 'Design', icon: Palette },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${activeTab === tab.id ? 'border-[#006b5f] text-[#006b5f]' : 'border-transparent text-slate-500'}`}>
              <tab.icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          ))}
        </div>

        {(error || success) && (
          <div className="p-3 space-y-2">
            {error && <div className="p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 break-words">{error}</div>}
            {success && <div className="p-2 bg-teal-50 border border-teal-100 rounded-lg text-xs text-teal-700">{success}</div>}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 sm:px-[max(1rem,calc((100%-42rem)/2))] lg:px-4 space-y-5">
          {activeTab === 'content' && (
            <>
              <button
                onClick={() => setShowImport(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-[#006b5f] bg-[#006b5f]/5 hover:bg-[#006b5f]/10 border border-[#006b5f]/30 rounded-lg transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Importer un menu avec l'IA
              </button>

              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center overflow-hidden">
                    {page.logo ? <img src={page.logo} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-400" />}
                  </div>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  <button onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center">
                    {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin text-[#006b5f]" /> : <Camera className="w-3 h-3 text-[#006b5f]" />}
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-semibold text-navy mb-1">Nom du menu</label>
                  <input type="text" value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })} className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Slogan (facultatif)</label>
                <input type="text" value={settings.tagline || ''} onChange={(e) => updateSettings('tagline', e.target.value)}
                  placeholder="Cuisine marocaine maison" className={inputCls} />
              </div>

              <div className="rounded-xl border border-slate-200 p-3 space-y-2.5 bg-slate-50/50">
                <input ref={heroInputRef} type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" />
                <label className="block text-xs font-semibold text-navy">Image d'accueil</label>
                <div className="flex items-center gap-3">
                  {settings.heroImage && <img src={settings.heroImage} alt="" className="w-16 h-10 rounded-lg object-cover" />}
                  <button type="button" onClick={() => heroInputRef.current?.click()} disabled={uploadingHero}
                    className="text-xs font-semibold text-[#006b5f] hover:underline">
                    {uploadingHero ? <Loader2 className="w-3 h-3 animate-spin inline" /> : settings.heroImage ? 'Changer' : 'Choisir une image'}
                  </button>
                  {settings.heroImage && (
                    <button type="button" onClick={() => updateSettings('heroImage', '')} className="text-xs text-slate-400 hover:text-red-500">Retirer</button>
                  )}
                </div>
                <input type="text" className={smallCls} value={settings.heroText || ''}
                  onChange={(e) => updateSettings('heroText', e.target.value)} placeholder="Phrase sur l'image (ex : Des saveurs authentiques…)" />

                <label className="block text-xs font-semibold text-navy pt-1">Langues du menu</label>
                <div className="flex gap-3">
                  {[['fr', 'Français'], ['en', 'English'], ['ar', 'العربية']].map(([code, label]) => {
                    const list = settings.languages?.length ? settings.languages : ['fr']
                    const on = list.includes(code)
                    return (
                      <label key={code} className="flex items-center gap-1.5 text-xs text-navy cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 accent-[#006b5f]" checked={on}
                          onChange={() => {
                            const next = on ? list.filter((l) => l !== code) : [...list, code]
                            updateSettings('languages', next.length ? next : ['fr'])
                          }} />
                        {label}
                      </label>
                    )
                  })}
                </div>

                <label className="block text-xs font-semibold text-navy pt-1">Infos pratiques (onglet « Plus »)</label>
                <input type="text" className={smallCls} value={settings.infoPhone || ''} onChange={(e) => updateSettings('infoPhone', e.target.value)} placeholder="Téléphone" />
                <input type="text" className={smallCls} value={settings.infoAddress || ''} onChange={(e) => updateSettings('infoAddress', e.target.value)} placeholder="Adresse" />
                <textarea rows={2} className={`${smallCls} resize-none`} value={settings.infoHours || ''} onChange={(e) => updateSettings('infoHours', e.target.value)} placeholder={'Horaires (ex : Lun–Dim 12h–23h)'} />
                <input type="text" className={smallCls} value={settings.infoWifi || ''} onChange={(e) => updateSettings('infoWifi', e.target.value)} placeholder="Code Wi-Fi" />
                <input type="text" className={smallCls} value={settings.infoInstagram || ''} onChange={(e) => updateSettings('infoInstagram', e.target.value)} placeholder="@instagram" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Lien de la page</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                  <span className="text-slate-400 shrink-0">tapio.link/menu/</span>
                  <input type="text" value={page.slug}
                    onChange={(e) => setPage({ ...page, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    placeholder={slugify(page.title) || 'mon-restaurant'}
                    className="flex-1 min-w-0 bg-transparent outline-none text-navy" />
                </div>
              </div>

              <div className="flex items-end justify-between gap-4">
                <div>
                  <label className="block text-xs font-semibold text-navy mb-1">Devise</label>
                  <input type="text" value={page.currency} onChange={(e) => setPage({ ...page, currency: e.target.value })}
                    className="w-24 px-3 py-2 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
                </div>
                <label className="flex items-center gap-2 pb-2 text-xs font-semibold text-navy cursor-pointer">
                  <input type="checkbox" checked={page.is_published}
                    onChange={(e) => setPage({ ...page, is_published: e.target.checked })}
                    className="w-4 h-4 accent-[#006b5f]" />
                  Page publiée
                </label>
              </div>

              {/* Commandes */}
              <div className="rounded-xl border border-slate-200 p-3 space-y-2.5 bg-slate-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-navy cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#006b5f]"
                    checked={settings.orderEnabled !== false}
                    onChange={(e) => updateSettings('orderEnabled', e.target.checked)} />
                  Activer les commandes (panier)
                </label>
                {settings.orderEnabled !== false && (
                  <>
                    <label className="flex items-center gap-2 text-xs text-navy cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-[#006b5f]"
                        checked={settings.askTable !== false}
                        onChange={(e) => updateSettings('askTable', e.target.checked)} />
                      Demander le numéro de table
                    </label>
                    <div>
                      <label className="block text-[11px] font-semibold text-navy mb-1">
                        WhatsApp de secours (facultatif)
                      </label>
                      <input type="text" inputMode="tel" className={smallCls}
                        value={settings.whatsapp || ''}
                        onChange={(e) => updateSettings('whatsapp', e.target.value)}
                        placeholder="212600000000" />
                      <p className="text-[10px] text-slate-400 mt-1">
                        Utilisé si le serveur est injoignable. Indicatif pays, sans + ni espaces.
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="space-y-3">
                <input ref={itemImageInputRef} type="file" accept="image/*" onChange={handleItemImageUpload} className="hidden" />
                {page.categories.map((cat, ci) => (
                  <div key={cat.id} className="border border-slate-200 rounded-xl p-3 space-y-3 bg-slate-50/50">
                    <div className="flex items-center gap-1">
                      <input type="text" value={cat.name || ''} onChange={(e) => updateCategory(cat.id, 'name', e.target.value)}
                        placeholder="Nom de la catégorie"
                        className="flex-1 min-w-0 px-2 py-1.5 bg-white rounded-lg text-sm font-semibold border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
                      <button onClick={() => setCategories((c) => move(c, ci, -1))} disabled={ci === 0} className="p-1.5 text-slate-400 hover:text-[#006b5f] disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => setCategories((c) => move(c, ci, 1))} disabled={ci === page.categories.length - 1} className="p-1.5 text-slate-400 hover:text-[#006b5f] disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                      <button onClick={() => removeCategory(cat.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>

                    <div className="space-y-2 pl-2">
                      {(cat.items || []).map((item, ii) => (
                        <div key={item.id} className={`border rounded-lg p-2.5 bg-white space-y-2 ${item.uncertain ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}>
                          {item.uncertain && (
                            <div className="text-[10px] font-semibold text-amber-600">⚠ Lecture incertaine — à vérifier</div>
                          )}
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                              {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-400" />}
                            </div>
                            <button onClick={() => requestItemImageUpload(cat.id, item.id)} className="text-[11px] text-[#006b5f] font-semibold hover:underline">
                              {uploadingItem === item.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : item.image ? 'Changer' : 'Photo'}
                            </button>
                            {item.image && (
                              <button onClick={() => updateItem(cat.id, item.id, 'image', '')} className="text-[11px] text-slate-400 hover:text-red-500">Retirer</button>
                            )}
                            <div className="ml-auto flex items-center">
                              <button onClick={() => mapItems(cat.id, (items) => move(items, ii, -1))} disabled={ii === 0} className="p-1.5 text-slate-400 hover:text-[#006b5f] disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                              <button onClick={() => mapItems(cat.id, (items) => move(items, ii, 1))} disabled={ii === (cat.items || []).length - 1} className="p-1.5 text-slate-400 hover:text-[#006b5f] disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                              <button onClick={() => removeItem(cat.id, item.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                          <input type="text" value={item.name || ''} onChange={(e) => updateItem(cat.id, item.id, 'name', e.target.value)} placeholder="Nom du plat" className={smallCls} />
                          <textarea rows={2} value={item.description || ''} onChange={(e) => updateItem(cat.id, item.id, 'description', e.target.value)} placeholder="Description (facultatif)" className={`${smallCls} resize-none`} />
                          <input type="text" inputMode="decimal" value={item.price ?? ''} onChange={(e) => updateItem(cat.id, item.id, 'price', e.target.value)} placeholder="Prix"
                            className="w-24 px-2 py-1.5 bg-slate-50 rounded-lg text-xs border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
                          <div className="flex flex-wrap gap-1">
                            {TAG_OPTIONS.map(([t, label]) => {
                              const on = (item.tags || []).includes(t)
                              return (
                                <button key={t} type="button" onClick={() => toggleTag(cat.id, item, t)} aria-pressed={on}
                                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${on ? 'bg-[#006b5f] border-[#006b5f] text-white' : 'border-slate-200 text-slate-500 hover:border-[#006b5f]'}`}>
                                  {label}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                      <button onClick={() => addItem(cat.id)}
                        className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-[#006b5f] border border-dashed border-[#006b5f]/40 rounded-lg hover:bg-[#006b5f]/5">
                        <Plus className="w-3.5 h-3.5" /> Ajouter un plat
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={addCategory}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-[#006b5f] hover:bg-[#005a50] rounded-lg transition-colors">
                <Plus className="w-4 h-4" /> Ajouter une catégorie
              </button>
            </>
          )}

          {activeTab === 'design' && (
            <>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Style du menu</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                {PRESET_LIST.map((p) => {
                  const on = settings.designPreset === p.id && !settings.customColors
                  return (
                    <button key={p.id} type="button"
                      onClick={() => setPage((pg) => ({ ...pg, settings: { ...pg.settings, designPreset: p.id, customColors: false, cardColorEnabled: false } }))}
                      className={`flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-colors ${on ? 'border-[#006b5f] bg-[#006b5f]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                      <span className="flex h-10 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200">
                        <span className="flex-1" style={{ background: p.tokens.bg }} />
                        <span className="w-4" style={{ background: p.tokens.accent }} />
                        <span className="w-3" style={{ background: p.tokens.price }} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-xs font-bold text-navy">{p.name}</span>
                        <span className="block text-[10px] text-slate-500 mt-0.5">{p.description}</span>
                      </span>
                    </button>
                  )
                })}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Cartes des plats</h3>
              <div className="grid grid-cols-3 gap-2">
                {ITEM_LAYOUTS.map((l) => (
                  <button key={l.id} type="button" onClick={() => updateSettings('itemLayout', l.id)}
                    className={`text-left p-2.5 rounded-xl border-2 transition-colors ${(settings.itemLayout || 'list') === l.id ? 'border-[#006b5f] bg-[#006b5f]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="text-xs font-bold text-navy">{l.name}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{l.description}</div>
                  </button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-4">Modèles professionnels</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-2">
                {MENU_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => setPage((p) => ({ ...p, settings: { ...p.settings, ...template.settings } }))}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-left hover:border-[#006b5f] hover:bg-slate-50 transition-colors"
                  >
                    <span className="h-10 w-10 shrink-0 rounded-lg border" style={{
                      background: template.settings.customBg,
                      boxShadow: `inset 0 -5px 0 ${template.settings.customAccent}`,
                      borderColor: template.settings.cardBorderColor,
                    }} />
                    <span className="min-w-0">
                      <span className="block text-xs font-bold text-navy">{template.name}</span>
                      <span className="block text-[10px] text-slate-500 mt-0.5">{template.description}</span>
                    </span>
                    <Sparkles className="w-4 h-4 ml-auto shrink-0 text-amber-500" />
                  </button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-4">Thème</h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 gap-2">
                {THEMES.map((t) => (
                  <button key={t.id}
                    onClick={() => setPage((p) => ({ ...p, settings: { ...p.settings, theme: t.id, customColors: false } }))}
                    className={`aspect-square rounded-xl border-2 transition-all ${settings.theme === t.id && !settings.customColors ? 'border-[#006b5f] scale-105' : 'border-transparent hover:border-slate-200'}`}
                    style={{ background: t.bgType === 'gradient' ? `linear-gradient(135deg, ${t.bg}, ${t.bg2})` : t.bg }}
                    title={t.name} />
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Couleurs custom</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!settings.customColors} onChange={(e) => updateSettings('customColors', e.target.checked)} className="w-4 h-4 rounded accent-[#006b5f]" />
                <span className="text-xs font-semibold text-navy">Activer</span>
              </label>
              {settings.customColors && (
                <div className="space-y-3">
                  <ColorInput label="Fond" value={settings.customBg} onChange={(v) => updateSettings('customBg', v)} />
                  <ColorInput label="Accent (prix / boutons / catégories)" value={settings.customAccent} onChange={(v) => updateSettings('customAccent', v)} />
                  <ColorInput label="Texte" value={settings.customText} onChange={(v) => updateSettings('customText', v)} />
                </div>
              )}

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">
                <Layers className="w-3 h-3 inline mr-1" />Couleur des cartes
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!settings.cardColorEnabled} onChange={(e) => updateSettings('cardColorEnabled', e.target.checked)} className="w-4 h-4 rounded accent-[#006b5f]" />
                <span className="text-xs font-semibold text-navy">Activer</span>
              </label>
              {settings.cardColorEnabled && (
                <div className="space-y-3">
                  <ColorInput label="Fond des cartes" value={settings.cardColor} onChange={(v) => updateSettings('cardColor', v)} />
                  <ColorInput label="Bordure des cartes" value={settings.cardBorderColor} onChange={(v) => updateSettings('cardBorderColor', v)} />
                </div>
              )}

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Police</h3>
              <select value={settings.font} onChange={(e) => updateSettings('font', e.target.value)} className={inputCls}>
                {FONTS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>

            </>
          )}
        </div>
      </aside>

      {/* ═══ APERÇU ═══ */}
      <main className={`${mobileView === 'edit' ? 'hidden' : 'flex'} lg:flex flex-1 min-w-0 lg:ml-[400px] items-center justify-center px-3 py-4 lg:py-10 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-10`}>
        {/* translateZ(0) : les feuilles (fiche plat, panier) restent contenues dans le téléphone */}
        <div
          className="w-full max-w-[340px] h-[clamp(420px,calc(100dvh-12rem),700px)] lg:h-[700px] rounded-[2rem] sm:rounded-[2.5rem] border-8 border-slate-900 overflow-hidden shadow-2xl relative bg-white"
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="w-full h-full overflow-y-auto">
            <MenuRenderer
              page={{
                title: page.title,
                avatar: page.logo,
                slug: page.slug,
                settings: { ...page.settings, currency: page.currency, categories: page.categories },
              }}
              preview
            />
          </div>
        </div>
      </main>

      {/* ═══ BARRE DU BAS (< lg) : Éditer / Aperçu / Enregistrer ═══ */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-100 pb-[env(safe-area-inset-bottom)]">
        <div className="h-14 grid grid-cols-3 items-stretch">
          <button onClick={() => setMobileView('edit')}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${mobileView === 'edit' ? 'text-[#006b5f]' : 'text-slate-500'}`}>
            <Layers className="w-5 h-5" />Éditer
          </button>
          <button onClick={() => setMobileView('preview')}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${mobileView === 'preview' ? 'text-[#006b5f]' : 'text-slate-500'}`}>
            <Eye className="w-5 h-5" />Aperçu
          </button>
          <button onClick={handleSave} disabled={isSaving}
            className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-white bg-[#006b5f] active:bg-[#005a50] disabled:opacity-60">
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}Enregistrer
          </button>
        </div>
      </nav>

      {showQrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowQrModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-xs w-full text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-navy mb-4">QR Code du menu</h3>
            <div className="flex justify-center mb-4 p-4 bg-white border border-slate-100 rounded-xl">
              <QRCodeSVG value={publicUrl} size={180} />
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-xs mb-3">
              <span className="truncate flex-1 text-slate-500">{publicUrl}</span>
              <button onClick={copyLink} aria-label="Copier le lien">
                {copied ? <Check className="w-3.5 h-3.5 text-teal-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
              </button>
            </div>
            <button onClick={() => setShowQrModal(false)} className="w-full py-2 bg-[#006b5f] text-white rounded-lg text-sm font-semibold">Fermer</button>
          </div>
        </div>
      )}

      {showImport && (
        <MenuImportWizard
          onCancel={() => setShowImport(false)}
          onDone={handleImportDone}
        />
      )}
    </div>
  )
}